import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { useSeo, setJsonLd, organizationSchema, websiteSchema } from "@/lib/seo";

const FAQ = [
  [
    "Which engines do you monitor today?",
    "OpenAI (ChatGPT), Anthropic (Claude) and Google (Gemini) via live APIs. Perplexity and Google AI Overviews are supported via instrumented scrapers — every answer is timestamped, attributed and stored with its full citation list.",
  ],
  [
    "Do I need my own API keys?",
    "No. AnswerWatch ships with a universal LLM key so you can start tracking in one click. Bring your own keys later to push margins higher.",
  ],
  [
    "How is the Visibility Score calculated?",
    "A 0–100 composite of mention rate, position quality, sentiment and citation strength — weighted by buyer-intent on each prompt.",
  ],
  [
    "Can I cancel anytime?",
    "Yes. Monthly plans cancel monthly with no fee. Annual plans cancel at the end of the term — we don't do auto-renew traps.",
  ],
];

const ENGINES = [
  { key: "chatgpt", name: "ChatGPT", letter: "G", bg: "#E8F4F0", fg: "#10A37F" },
  { key: "claude", name: "Claude", letter: "C", bg: "#FBEBE2", fg: "#D97757" },
  { key: "perplexity", name: "Perplexity", letter: "p", bg: "#E5F0F2", fg: "#20808D" },
  { key: "gemini", name: "Gemini", letter: "g", bg: "#E8EFFB", fg: "#4285F4" },
  { key: "aio", name: "Google AI Overviews", letter: "A", bg: "#EFEAFA", fg: "#7B5BD9" },
];

const TICKER_ITEMS = [
  ["Trimly", "appeared in ChatGPT answer · \"best GLP-1 telehealth\"", true],
  ["Linear", "overtook Jira on Perplexity for \"best issue tracker\"", false],
  ["Vercel", "cited by Claude in 14 / 20 deployment prompts", false],
  ["Ramp", "+18 pts visibility in 7 days", true],
  ["Notion", "dropped from Gemini answer · \"best wiki\"", false],
  ["Stripe", "#1 across all 5 engines · \"payment infrastructure\"", false],
];

function ScanCard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("ready"); // ready | running | complete
  const [engineStates, setEngineStates] = useState(
    ENGINES.map(() => ({ phase: "awaiting", position: null })) // phase: awaiting | scanning | mentioned | missed | error
  );
  const [scan, setScan] = useState(null); // { scan_id, brand, results }
  const [showLead, setShowLead] = useState(false);
  const [email, setEmail] = useState("");
  const [submittingLead, setSubmittingLead] = useState(false);
  const promptRef = useRef(null);
  const domainRef = useRef(null);

  const reset = () =>
    setEngineStates(ENGINES.map(() => ({ phase: "awaiting", position: null })));

  const run = async (e) => {
    e.preventDefault();
    if (status === "running") return;
    const prompt = (promptRef.current?.value || "").trim();
    const domain = (domainRef.current?.value || "").trim();
    if (!prompt || !domain) return;

    setStatus("running");
    setScan(null);
    reset();
    // Mark all 5 engines as scanning immediately — CSS animation-delay on
    // each .scan-engine.scanning .stat .ind creates the staggered pulse feel.
    setEngineStates(
      ENGINES.map(() => ({ phase: "scanning", position: null }))
    );

    try {
      const res = await api.post("/public/prompt-scan", {
        prompt,
        domain,
        language: "en-GB",
      });
      const data = res.data;
      setScan(data);
      // Map backend results back to engine order
      const byEngine = {};
      (data.results || []).forEach((r) => {
        byEngine[r.engine] = r;
      });
      setEngineStates(
        ENGINES.map((eng) => {
          const r = byEngine[eng.key];
          if (!r) return { phase: "missed", position: null };
          if (r.error) return { phase: "error", position: null };
          return r.mentioned
            ? { phase: "mentioned", position: r.position || null }
            : { phase: "missed", position: null };
        })
      );
      setStatus("complete");
      // Open lead-capture after a short pause so the user sees their results
      setTimeout(() => setShowLead(true), 900);
    } catch (err) {
      setStatus("ready");
      reset();
      const msg =
        err?.response?.data?.detail ||
        "Scan failed. Please try again in a moment.";
      toast.error(msg);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (submittingLead) return;
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@") || !trimmed.includes(".")) {
      toast.error("Enter a valid email.");
      return;
    }
    setSubmittingLead(true);
    try {
      const res = await api.post("/public/prompt-scan/lead", {
        email: trimmed,
        scan_id: scan?.scan_id,
      });
      // Persist prefill so Onboarding can read it after auth0 round-trip
      window.sessionStorage.setItem(
        "aw_scan_prefill",
        JSON.stringify({
          prompt: res.data?.prefill?.prompt || promptRef.current?.value,
          domain: res.data?.prefill?.domain || domainRef.current?.value,
          brand: res.data?.prefill?.brand,
          scan_id: scan?.scan_id,
          email: trimmed,
          captured_at: new Date().toISOString(),
        })
      );
      toast.success("Got it! Taking you to your dashboard…");
      // Send to login (which round-trips through Auth0, lands at dashboard)
      navigate("/login");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save email. Try again.";
      toast.error(msg);
    } finally {
      setSubmittingLead(false);
    }
  };

  const statusLabel =
    status === "running" ? "Running" : status === "complete" ? "Complete" : "Ready";

  return (
    <div className="scan-card" data-testid="landing-scan-card">
      <div className="scan-top">
        <div className="scan-top-left">
          <span className="scan-dots">
            <span></span>
            <span></span>
            <span></span>
          </span>
          <span>Live scan · 5 engines</span>
        </div>
        <span className="scan-status" data-testid="scan-status">
          <span className={`pulse ${status === "running" ? "dot-pulse" : ""}`}></span>
          {statusLabel}
        </span>
      </div>
      <form onSubmit={run} className="px-6 pt-6 pb-5">
        <div className="scan-field mb-4">
          <label htmlFor="aw-prompt">Buyer question</label>
          <input
            ref={promptRef}
            id="aw-prompt"
            name="prompt"
            type="text"
            placeholder='e.g. "best CRM for European B2B teams"'
            defaultValue="best telehealth provider for GLP-1"
            className="scan-input"
            data-testid="scan-prompt"
            required
            maxLength={280}
          />
        </div>
        <div className="scan-field mb-5">
          <label htmlFor="aw-domain">Your domain</label>
          <input
            ref={domainRef}
            id="aw-domain"
            name="domain"
            type="text"
            placeholder="yourcompany.com"
            defaultValue="trimly.com"
            className="scan-input"
            data-testid="scan-domain"
            required
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span
            className="mono"
            style={{ fontSize: 10.5, letterSpacing: "0.05em", color: "var(--aw-muted)" }}
          >
            Tests <b style={{ color: "var(--aw-ink)", fontWeight: 500 }}>1 prompt × 5 engines</b> ·
            results in ~{status === "running" ? "20-30" : "20"}s
          </span>
          <button
            type="submit"
            className="scan-cta"
            disabled={status === "running"}
            data-testid="scan-submit"
          >
            {status === "complete"
              ? "Run again"
              : status === "running"
              ? "Scanning…"
              : "Run scan"}
            {status !== "running" && <span className="mono">→</span>}
          </button>
        </div>
      </form>
      <div
        className="px-5 pt-3 pb-4 border-t"
        style={{
          borderColor: "var(--aw-border)",
          background: "color-mix(in oklch, var(--aw-bg) 60%, white)",
        }}
      >
        <div
          className="flex items-center justify-between px-1.5 mb-2"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--aw-muted)",
          }}
        >
          <span>Engines</span>
          <span style={{ color: "var(--aw-ink)", fontWeight: 500 }}>
            {status === "complete" && scan
              ? `${scan.mentioned_count} / ${scan.engines_count} mentioned`
              : `${ENGINES.length} / ${ENGINES.length} ready`}
          </span>
        </div>
        {ENGINES.map((eng, idx) => {
          const s = engineStates[idx];
          const cls =
            s.phase === "scanning"
              ? "scanning"
              : s.phase === "mentioned"
              ? "done"
              : s.phase === "missed"
              ? "done missed"
              : s.phase === "error"
              ? "done missed"
              : "";
          return (
            <div
              key={eng.key}
              className={`scan-engine ${cls}`}
              data-testid={`scan-engine-${eng.key}`}
              style={
                s.phase === "scanning"
                  ? { animationDelay: `${idx * 120}ms` }
                  : {}
              }
            >
              <span
                className="glyph"
                style={{ background: eng.bg, color: eng.fg }}
              >
                {eng.letter}
              </span>
              <span className="name">{eng.name}</span>
              <span className="stat">
                <span
                  className="ind"
                  style={s.phase === "scanning" ? { animationDelay: `${idx * 120}ms` } : {}}
                />
                {s.phase === "awaiting" && "Awaiting"}
                {s.phase === "scanning" && "Querying…"}
                {s.phase === "mentioned" && (
                  <>
                    Mentioned{s.position ? <> · <b>#{s.position}</b></> : null}
                  </>
                )}
                {s.phase === "missed" && "Not mentioned"}
                {s.phase === "error" && "Unavailable"}
              </span>
            </div>
          );
        })}
      </div>
      <div
        className="flex items-center justify-between px-5 py-3 border-t"
        style={{
          borderColor: "var(--aw-border)",
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.05em",
          color: "var(--aw-muted)",
          background: "white",
        }}
      >
        <span className="inline-flex items-center gap-1.5">
          <svg
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            width="11"
            height="11"
          >
            <rect x="2.5" y="5.5" width="7" height="5" rx="1" />
            <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" />
          </svg>
          {status === "complete"
            ? "Live AI scan · results saved"
            : "Live API call · prompt is never stored"}
        </span>
        <span>
          {status === "complete" && scan?.duration_ms
            ? `${(scan.duration_ms / 1000).toFixed(1)}s`
            : "Real engines, real time"}
        </span>
      </div>

      {/* Lead-capture modal */}
      {showLead && (
        <LeadModal
          scan={scan}
          email={email}
          setEmail={setEmail}
          submitting={submittingLead}
          onSubmit={submitLead}
          onClose={() => setShowLead(false)}
        />
      )}
    </div>
  );
}

function LeadModal({ scan, email, setEmail, submitting, onSubmit, onClose }) {
  return (
    <div
      data-testid="scan-lead-modal"
      role="dialog"
      aria-label="See your full report"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "rgba(20, 18, 12, 0.45)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        animation: "fadeIn 0.18s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--aw-bg)",
          borderRadius: 14,
          boxShadow:
            "0 24px 64px -16px rgba(20, 18, 12, 0.3), 0 8px 24px -8px rgba(20, 18, 12, 0.15)",
          maxWidth: 480,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "28px 28px 20px",
            borderBottom: "1px solid var(--aw-border)",
          }}
        >
          <div className="eyebrow">Your scan</div>
          <h2
            className="mt-2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 28,
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
              margin: 0,
            }}
          >
            {scan?.mentioned_count > 0 ? (
              <>
                {scan.brand} appeared in{" "}
                <em style={{ color: "var(--aw-accent-ink)" }}>
                  {scan.mentioned_count} of {scan.engines_count}
                </em>{" "}
                engines.
              </>
            ) : (
              <>
                {scan?.brand || "Your brand"} wasn't in any of the{" "}
                <em style={{ color: "var(--aw-accent-ink)" }}>{scan?.engines_count}</em>{" "}
                AI answers.
              </>
            )}
          </h2>
          <p
            className="mt-3 text-sm"
            style={{ color: "var(--aw-muted)", lineHeight: 1.55, margin: "12px 0 0" }}
          >
            Drop your email to see the full answers, citations, and a 14-day
            free trial of the dashboard. We'll pre-fill your project with this
            scan.
          </p>
        </div>
        <form onSubmit={onSubmit} style={{ padding: 24 }}>
          <div className="scan-field mb-4">
            <label htmlFor="lead-email">Work email</label>
            <input
              id="lead-email"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="scan-input"
              data-testid="lead-email"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="scan-cta w-full"
            style={{ width: "100%", justifyContent: "center" }}
            data-testid="lead-submit"
          >
            {submitting ? "Sending…" : "See full report"}{" "}
            {!submitting && <span className="mono">→</span>}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full mt-3"
            data-testid="lead-skip"
            style={{
              background: "transparent",
              border: 0,
              color: "var(--aw-muted)",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Skip — view summary only
          </button>
          <div
            className="mono mt-3 text-center"
            style={{ fontSize: 10, color: "var(--aw-muted)", letterSpacing: "0.05em" }}
          >
            We never share your email. Cancel anytime.
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Landing() {
  useSeo({
    title: "AnswerWatch — Rank tracking for AI answer engines",
    description:
      "AnswerWatch monitors how ChatGPT, Claude, Perplexity, Gemini and Google AI Overviews answer buyer-stage questions in your category — and tells you exactly what to publish to start being recommended.",
    path: "/",
  });

  React.useEffect(() => {
    setJsonLd("ld-organization", organizationSchema());
    setJsonLd("ld-website", websiteSchema());
    setJsonLd("ld-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map(([q, a]) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
  }, []);

  return (
    <div data-testid="landing-page" style={{ background: "var(--aw-bg)" }}>
      <MarketingNav />

      {/* HERO */}
      <section style={{ padding: "80px 0 56px", position: "relative", overflow: "hidden" }}>
        <div className="grid-bg absolute inset-0 pointer-events-none" />
        <div className="max-w-[1180px] mx-auto px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-[72px] items-center">
            <div className="reveal">
              <span
                className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11.5,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "var(--aw-muted)",
                  border: "1px solid var(--aw-border)",
                  background: "rgba(255,255,255,0.5)",
                  backdropFilter: "blur(8px)",
                }}
                data-testid="hero-kicker"
              >
                <span className="dot dot-pulse"></span> AnswerWatch · v2.4 live across 5 engines
              </span>
              <h1 className="aw-h-display mt-6" data-testid="hero-headline">
                Best AI Visibility Tool.
                <br />
                Rank in <em>answers</em>,
                <br />
                not in <span className="strike">links</span>.
              </h1>
              <p
                style={{
                  fontSize: 17.5,
                  lineHeight: 1.55,
                  color: "var(--aw-muted)",
                  maxWidth: 540,
                  margin: "24px 0 0",
                }}
              >
                Buyers stopped clicking. They ask ChatGPT, Gemini, Perplexity, Claude and Google AI Overviews —
                and act on the answer. AnswerWatch tells you whether you're in it, who is, and exactly what to ship to get there.
              </p>
              <div
                className="flex items-center gap-5 mt-7"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11.5,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "var(--aw-muted)",
                  flexWrap: "wrap",
                }}
              >
                <span>
                  <strong style={{ color: "var(--aw-ink)", fontWeight: 500 }}>2,400+</strong> brands tracked
                </span>
                <span style={{ width: 4, height: 4, borderRadius: 999, background: "var(--aw-border)" }}></span>
                <span>
                  <strong style={{ color: "var(--aw-ink)", fontWeight: 500 }}>5</strong> engines, daily
                </span>
                <span style={{ width: 4, height: 4, borderRadius: 999, background: "var(--aw-border)" }}></span>
                <span>No credit card</span>
              </div>
              <div className="mt-8 flex items-center gap-3 flex-wrap">
                <Link to="/login" data-testid="hero-cta" className="btn-aw btn-aw-primary">
                  Start free <span className="arrow">→</span>
                </Link>
                <Link to="/atlas" data-testid="hero-atlas" className="btn-aw">
                  Free AI Atlas
                </Link>
              </div>
            </div>
            <div className="reveal reveal-2">
              <ScanCard />
            </div>
          </div>
        </div>
      </section>

      {/* LIVE TICKER */}
      <section
        style={{
          borderTop: "1px solid var(--aw-border)",
          borderBottom: "1px solid var(--aw-border)",
          background: "color-mix(in oklch, var(--aw-bg) 60%, white)",
          overflow: "hidden",
        }}
        data-testid="landing-ticker"
      >
        <div
          className="max-w-[1180px] mx-auto px-6 flex items-center gap-5"
          style={{ padding: "14px 24px" }}
        >
          <span
            className="mono"
            style={{
              fontSize: 10.5,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--aw-muted)",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span className="dot dot-pulse"></span>
            Live · last hour
          </span>
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              maskImage:
                "linear-gradient(90deg, transparent, black 4%, black 96%, transparent)",
              WebkitMaskImage:
                "linear-gradient(90deg, transparent, black 4%, black 96%, transparent)",
            }}
          >
            <div className="ticker-track" style={{ fontSize: 13 }}>
              {[...TICKER_ITEMS, ...TICKER_ITEMS].map(([brand, body, accent], i) => (
                <span key={i}>
                  <b style={{ color: accent ? "var(--aw-accent-ink)" : "var(--aw-ink)" }}>
                    {brand}
                  </b>{" "}
                  {body}
                  <span style={{ color: "var(--aw-muted)", marginLeft: 28 }}>·</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ENGINES STRIP */}
      <section style={{ padding: "64px 0 32px" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center mb-7">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              Tracks every major AI engine
            </div>
          </div>
          <div className="flex items-center justify-center gap-3.5 flex-wrap">
            {ENGINES.map((eng) => (
              <span key={eng.key} className="engine-chip">
                <span
                  className="ai-glyph"
                  style={{
                    background: eng.bg,
                    color: eng.fg,
                    width: 18,
                    height: 18,
                    fontSize: 10,
                  }}
                >
                  {eng.letter}
                </span>
                {eng.name}
              </span>
            ))}
            <span className="engine-chip" style={{ color: "var(--aw-muted)" }}>
              + Copilot, Grok, You.com soon
            </span>
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section style={{ padding: "96px 0" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">
            <div className="aw-sticky">
              <div className="eyebrow mb-4">Why this matters</div>
              <h2 className="aw-h1 mb-5">
                Google sent buyers to your website.
                <br />
                <em>AI answers the question for them.</em>
              </h2>
              <p style={{ fontSize: 19, lineHeight: 1.5, color: "var(--aw-ink-2)", maxWidth: 540 }}>
                By 2027, half of all buyer research will happen inside an AI chat. If you're not in those
                answers, you're not in the consideration set — and you'll never see the click that didn't happen.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="card-aw">
                <div className="eyebrow">Before</div>
                <h3
                  className="mt-3 mb-2"
                  style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 400, lineHeight: 1.25 }}
                >
                  User searches Google →
                </h3>
                <p style={{ margin: 0, fontSize: 14.5, color: "var(--aw-muted)" }}>
                  10 blue links. They visit your site. You measure the click.
                </p>
              </div>
              <div className="card-aw" style={{ background: "var(--aw-accent-soft)", borderColor: "transparent" }}>
                <div className="eyebrow" style={{ color: "var(--aw-accent-ink)" }}>
                  Now
                </div>
                <h3
                  className="mt-3 mb-2"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 24,
                    fontWeight: 400,
                    lineHeight: 1.25,
                    color: "var(--aw-accent-ink)",
                  }}
                >
                  User asks ChatGPT →
                </h3>
                <p style={{ margin: 0, fontSize: 14.5, color: "var(--aw-accent-ink)", opacity: 0.85 }}>
                  It synthesizes an answer. They never click. You can't see whether you were mentioned,
                  dismissed, or invisible.
                </p>
              </div>
              <div className="card-aw">
                <div className="eyebrow">With AnswerWatch</div>
                <h3
                  className="mt-3 mb-2"
                  style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 400, lineHeight: 1.25 }}
                >
                  You see every answer →
                </h3>
                <p style={{ margin: 0, fontSize: 14.5, color: "var(--aw-muted)" }}>
                  We run your buyer prompts across 5 AI engines daily. You see who's recommended, why,
                  and what to fix.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPOTLIGHT: sample answer + score card */}
      <section
        id="features"
        style={{
          padding: "96px 0",
          background: "color-mix(in oklch, var(--aw-bg) 70%, white)",
          borderTop: "1px solid var(--aw-border)",
        }}
      >
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="flex items-end justify-between gap-8 flex-wrap mb-10">
            <div style={{ maxWidth: 620 }}>
              <div className="eyebrow mb-4">The main view</div>
              <h2 className="aw-h1">
                Every answer. Every engine.
                <br />
                <em>One score that moves.</em>
              </h2>
            </div>
            <p
              style={{
                maxWidth: 360,
                margin: 0,
                fontSize: 19,
                lineHeight: 1.5,
                color: "var(--aw-ink-2)",
              }}
            >
              On the left, the raw AI answer with brand mentions extracted. On the right, your live
              Visibility Score and share-of-voice against every competitor.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6 items-stretch">
            <div className="ai-answer">
              <div className="ai-head">
                <span className="ai-glyph" style={{ background: "#E8F4F0", color: "#10A37F" }}>
                  G
                </span>
                <div style={{ flex: 1 }}>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      color: "var(--aw-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    ChatGPT · 4h ago
                  </div>
                  <div className="ai-prompt">
                    "What's the best telehealth provider for GLP-1 weight loss medication?"
                  </div>
                </div>
              </div>
              <div className="ai-body">
                <p>
                  For GLP-1 weight loss medication, the most-cited online providers in 2026 are{" "}
                  <span className="mention">Hims</span>, <span className="mention">Ro</span>, and{" "}
                  <span className="mention">Found</span>. Hims and Ro both offer compounded semaglutide
                  programs starting around $199/month, while Found pairs medication with behavioral coaching.
                </p>
                <p>
                  <span className="mention you">Trimly</span> is a newer entrant frequently praised in user
                  forums for shorter intake-to-shipment timelines (typically under 5 days) and transparent
                  flat-fee pricing.
                </p>
              </div>
              <div className="ai-cites">
                <span className="ai-cite">↳ reddit.com/r/Mounjaro</span>
                <span className="ai-cite">↳ healthline.com</span>
                <span className="ai-cite">↳ forhims.com</span>
                <span className="ai-cite">↳ ro.co</span>
                <span className="ai-cite">↳ trimly.com</span>
              </div>
            </div>

            <div className="ai-answer" style={{ background: "var(--aw-ink)", color: "var(--aw-bg)", borderColor: "var(--aw-ink)" }}>
              <div className="flex items-center justify-between mb-4">
                <div
                  className="mono"
                  style={{
                    fontSize: 11,
                    color: "#B8B3A6",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  trimly · last 28 days
                </div>
                <span
                  className="pill"
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    color: "white",
                    borderColor: "rgba(255,255,255,0.12)",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: "var(--aw-accent)",
                    }}
                  ></span>
                  Live
                </span>
              </div>
              <div className="flex items-end gap-6 mb-5">
                <div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      color: "#8A857A",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 6,
                    }}
                  >
                    Visibility score
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 86,
                      lineHeight: 1,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    52
                    <span style={{ fontSize: 22, color: "#8A857A" }}> / 100</span>
                  </div>
                </div>
                <div style={{ paddingBottom: 14 }}>
                  <div className="mono" style={{ fontSize: 12, color: "oklch(0.82 0.10 155)" }}>
                    ▲ 11.4 pts
                  </div>
                  <div style={{ fontSize: 12, color: "#8A857A", marginTop: 2 }}>vs. last month</div>
                </div>
              </div>
              <div style={{ borderTop: "1px dashed rgba(255,255,255,0.12)", paddingTop: 16 }}>
                <div
                  className="mono"
                  style={{
                    fontSize: 10.5,
                    color: "#8A857A",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: 10,
                  }}
                >
                  Share of voice — GLP-1 telehealth
                </div>
                <div className="flex flex-col gap-2">
                  {[
                    ["Hims", 88, "#C8C5BC", 28, false],
                    ["Ro", 72, "#A8A49C", 22, false],
                    ["Trimly", 48, "var(--aw-accent)", 14, true],
                    ["Found", 38, "#8A857A", 11, false],
                    ["Mochi", 32, "#75716A", 9, false],
                  ].map(([n, w, c, pct, isYou]) => (
                    <div
                      key={n}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "70px 1fr 36px",
                        gap: 10,
                        alignItems: "center",
                        fontSize: 13,
                        color: isYou ? "oklch(0.92 0.05 155)" : "#D8D4C7",
                      }}
                    >
                      <span>
                        {isYou && <span style={{ color: "var(--aw-accent)" }}>● </span>}
                        {n}
                      </span>
                      <span
                        style={{
                          height: 6,
                          borderRadius: 999,
                          background: "rgba(255,255,255,0.08)",
                          display: "block",
                          position: "relative",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: `${w}%`,
                            background: c,
                            borderRadius: 999,
                          }}
                        ></span>
                      </span>
                      <span
                        className="mono"
                        style={{
                          textAlign: "right",
                          color: isYou ? "oklch(0.92 0.05 155)" : "#C8C5BC",
                        }}
                      >
                        {pct}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 PILLARS */}
      <section style={{ padding: "96px 0", borderTop: "1px solid var(--aw-border)" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div style={{ maxWidth: 720, marginBottom: 56 }}>
            <div className="eyebrow mb-4">What you get</div>
            <h2 className="aw-h1">
              Four things SEO can't tell you
              <br />
              about your AI visibility.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Pillar 1 - Track */}
            <div className="card-aw card-aw-lg" data-testid="pillar-track">
              <div className="flex justify-between items-start mb-4">
                <span className="pill">01 · Track</span>
                <span className="mono" style={{ fontSize: 11, color: "var(--aw-muted)" }}>
                  /prompts
                </span>
              </div>
              <h3 className="aw-h2" style={{ fontSize: 30, marginBottom: 12 }}>
                Buyer prompts, rerun daily.
              </h3>
              <p style={{ margin: "0 0 24px", fontSize: 15, lineHeight: 1.55, color: "var(--aw-muted)" }}>
                Add the questions your buyers actually ask AI. We rerun each one across every engine, every
                24 hours, and store the full answer.
              </p>
              <div
                style={{
                  background: "var(--aw-bg)",
                  border: "1px solid var(--aw-border)",
                  borderRadius: 10,
                  padding: 14,
                }}
              >
                <div className="flex flex-col gap-2.5" style={{ fontSize: 13 }}>
                  {[
                    ["01", "Best telehealth provider for GLP-1…", "#2 / 6", false, 1],
                    ["02", "Cheapest online prescription for semaglutide", "#1 / 5", true, 1],
                    ["03", "Compare Hims vs Ro for weight loss", "#3 / 4", false, 1],
                    ["04", "Weight loss telehealth that takes insurance", "— / 5", false, 0.5],
                  ].map(([n, t, p, accent, opacity]) => (
                    <div key={n} className="flex gap-2.5 items-center" style={{ opacity }}>
                      <span className="mono" style={{ color: "var(--aw-muted)", width: 18 }}>
                        {n}
                      </span>
                      <span style={{ flex: 1, color: "var(--aw-ink-2)" }}>{t}</span>
                      <span
                        className={`pill ${accent ? "pill-accent" : ""}`}
                        style={{ height: 18, fontSize: 10.5 }}
                      >
                        {p}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pillar 2 - Compare */}
            <div className="card-aw card-aw-lg" data-testid="pillar-compare">
              <div className="flex justify-between items-start mb-4">
                <span className="pill">02 · Compare</span>
                <span className="mono" style={{ fontSize: 11, color: "var(--aw-muted)" }}>
                  /competitors
                </span>
              </div>
              <h3 className="aw-h2" style={{ fontSize: 30, marginBottom: 12 }}>
                Share of voice, by competitor.
              </h3>
              <p style={{ margin: "0 0 24px", fontSize: 15, lineHeight: 1.55, color: "var(--aw-muted)" }}>
                See exactly how often AI mentions you compared to every competitor — across engines, prompts, and over time.
              </p>
              {[
                ["Hims", 88, false],
                ["Ro", 72, false],
                ["Trimly", 48, true],
                ["Found", 38, false],
                ["Mochi", 32, false],
                ["Calibrate", 18, false],
              ].map(([n, v, you]) => (
                <div key={n} className="bar-row">
                  <span className={`label ${you ? "you" : ""}`}>
                    {you && "● "}
                    {n}
                  </span>
                  <div className="bar-track">
                    <div className={`bar-fill ${you ? "you" : ""}`} style={{ width: `${v}%` }}></div>
                  </div>
                  <span className="bar-val">{v}</span>
                </div>
              ))}
            </div>

            {/* Pillar 3 - Diagnose */}
            <div className="card-aw card-aw-lg" data-testid="pillar-diagnose">
              <div className="flex justify-between items-start mb-4">
                <span className="pill">03 · Diagnose</span>
                <span className="mono" style={{ fontSize: 11, color: "var(--aw-muted)" }}>
                  /citations
                </span>
              </div>
              <h3 className="aw-h2" style={{ fontSize: 30, marginBottom: 12 }}>
                Which sources AI reads.
              </h3>
              <p style={{ margin: "0 0 24px", fontSize: 15, lineHeight: 1.55, color: "var(--aw-muted)" }}>
                Every answer cites sources. We rank them — Reddit threads, Healthline articles, your own pages —
                so you know exactly where to invest.
              </p>
              <div className="flex flex-col gap-2">
                {[
                  ["reddit.com/r/Mounjaro", 412, false],
                  ["nytimes.com", 287, false],
                  ["healthline.com", 244, false],
                  ["trimly.com", 71, true],
                ].map(([src, n, you]) => (
                  <div
                    key={src}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 60px",
                      gap: 12,
                      alignItems: "center",
                      padding: "10px 12px",
                      background: you ? "var(--aw-accent-soft)" : "var(--aw-bg)",
                      border: you ? "1px solid transparent" : "1px solid var(--aw-border)",
                      borderRadius: 8,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 13,
                        color: you ? "var(--aw-accent-ink)" : "var(--aw-ink-2)",
                        fontWeight: you ? 600 : 400,
                      }}
                    >
                      {src}
                    </span>
                    <span
                      className="mono tnum"
                      style={{
                        fontSize: 12,
                        color: you ? "var(--aw-accent-ink)" : "var(--aw-ink)",
                        textAlign: "right",
                        fontWeight: you ? 600 : 400,
                      }}
                    >
                      {n}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pillar 4 - Fix */}
            <div className="card-aw card-aw-lg" data-testid="pillar-fix">
              <div className="flex justify-between items-start mb-4">
                <span className="pill">04 · Fix</span>
                <span className="mono" style={{ fontSize: 11, color: "var(--aw-muted)" }}>
                  /recommendations
                </span>
              </div>
              <h3 className="aw-h2" style={{ fontSize: 30, marginBottom: 12 }}>
                Specific things to ship.
              </h3>
              <p style={{ margin: "0 0 24px", fontSize: 15, lineHeight: 1.55, color: "var(--aw-muted)" }}>
                Not just a score. Every week we surface ranked, founder-readable fixes — content gaps, schema,
                PR pitches, Reddit threads — each with an estimated lift.
              </p>
              <ul className="taglist">
                <li>
                  <span className="num">↗ +12</span>
                  <div className="body">
                    <strong>Earn 3+ organic threads on r/Mounjaro</strong>
                    <span>
                      Reddit is the #1 cited domain across all 5 engines. Hims has 91 brand-tagged mentions,
                      you have 38.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="num">↗ +8</span>
                  <div className="body">
                    <strong>Get listed in Consumer Reports' 2026 roundup</strong>
                    <span>Cited in 31% of comparison prompts. You're absent.</span>
                  </div>
                </li>
                <li>
                  <span className="num">↗ +5</span>
                  <div className="body">
                    <strong>Publish a compounded vs. brand semaglutide explainer</strong>
                    <span>Add FAQ schema. AI answers cite first-party explainers 2.3× more.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BIG NUMBERS */}
      <section
        style={{
          padding: "96px 0",
          background: "var(--aw-surface-2)",
          borderTop: "1px solid var(--aw-border)",
          borderBottom: "1px solid var(--aw-border)",
        }}
      >
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center mb-14">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              By the numbers
            </div>
            <h2 className="aw-h1 mt-4">
              The AI visibility problem,
              <br />
              quantified.
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              ["58", "%", "of buyer research now starts with an AI chat, not a search bar."],
              ["0", "", "clicks generated by an AI answer that doesn't mention you."],
              ["5", "", "major answer engines, each citing different sources for the same query."],
              ["23", "×", "average month-over-month growth in branded prompts since GPT-4."],
            ].map(([n, unit, lbl]) => (
              <div key={lbl} className="text-center">
                <div className="stat-num">
                  {n}
                  {unit && <span style={{ fontSize: 28, color: "var(--aw-muted)" }}>{unit}</span>}
                </div>
                <div className="stat-lbl mx-auto">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section style={{ padding: "96px 0" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <p className="pull mx-auto text-center">
            "We spent two years optimizing for Google. Then half our pipeline started saying{" "}
            <em>'I asked ChatGPT and it recommended you.'</em> AnswerWatch is the first tool that actually tells
            us why."
          </p>
          <div className="flex items-center gap-3.5 justify-center mt-8">
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 999,
                background: "var(--aw-accent-soft)",
                color: "var(--aw-accent-ink)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--font-display)",
                fontSize: 18,
              }}
            >
              N
            </div>
            <div>
              <div style={{ fontWeight: 500 }}>Nadia Brennan</div>
              <div style={{ fontSize: 13, color: "var(--aw-muted)" }}>VP Growth, Held (B2B fintech)</div>
            </div>
          </div>
        </div>
      </section>

      {/* LOGO STRIP */}
      <section style={{ padding: "32px 0 80px" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="logos">
            <span>Trimly</span>
            <span>Linnea</span>
            <span>Held</span>
            <span>Northbound</span>
            <span>Folio</span>
            <span>Vellum</span>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" style={{ padding: "96px 0", borderTop: "1px solid var(--aw-border)" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div>
              <div className="eyebrow mb-4">How it works</div>
              <h2 className="aw-h1">
                From "are we in the answer?"
                <br />
                to "here's how to be" —
                <br />
                in <em>about an hour.</em>
              </h2>
            </div>
            <div>
              <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {[
                  ["01", "Tell us your brand & 3 competitors.", "We infer your category and pre-generate 50 buyer prompts."],
                  ["02", "We run them across 5 engines.", "~7 minutes for your first scan. After that, every 24h."],
                  ["03", "You get your Visibility Score.", "0–100 composite, broken down by engine, prompt, and source."],
                  ["04", "We ship you a fix list every Monday.", "Prioritized by lift and effort. Exportable to Linear, Notion, or email."],
                ].map(([n, t, b], i, arr) => (
                  <li
                    key={n}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "56px 1fr",
                      gap: 18,
                      padding: "22px 0",
                      borderBottom: i === arr.length - 1 ? "none" : "1px solid var(--aw-border)",
                    }}
                    data-testid={`how-step-${n}`}
                  >
                    <div
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: 36,
                        color: "var(--aw-accent-ink)",
                        lineHeight: 1,
                      }}
                    >
                      {n}
                    </div>
                    <div>
                      <h3
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: 18,
                          margin: "4px 0 4px",
                          fontWeight: 500,
                        }}
                      >
                        {t}
                      </h3>
                      <p style={{ margin: 0, fontSize: 14.5, color: "var(--aw-muted)" }}>{b}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section
        style={{
          padding: "64px 0",
          background: "var(--aw-surface-2)",
          borderTop: "1px solid var(--aw-border)",
          borderBottom: "1px solid var(--aw-border)",
        }}
      >
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center mb-9">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              AnswerWatch vs.
            </div>
            <h2 className="aw-h2 mt-4">Why this is not just SEO with a new wrapper.</h2>
          </div>
          <div
            className="overflow-x-auto"
            style={{
              background: "var(--aw-surface)",
              border: "1px solid var(--aw-border)",
              borderRadius: 14,
            }}
            data-testid="landing-comparison"
          >
            <div style={{ minWidth: 720 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1fr 1fr 1fr",
                padding: "14px 24px",
                borderBottom: "1px solid var(--aw-border)",
                background: "var(--aw-surface-2)",
              }}
            >
              <div></div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18 }}>Google Search Console</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18 }}>Generic rank trackers</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18, color: "var(--aw-accent-ink)" }}>
                <em>AnswerWatch</em>
              </div>
            </div>
            {[
              ["Tracks Google search rankings", "✓", "✓", "✓"],
              ["Tracks ChatGPT, Gemini, Claude, Perplexity", "—", "partial", "✓"],
              ["Full AI answer text + citations", "—", "—", "✓"],
              ["Competitive share of voice", "—", "✓", "✓"],
              ["Sentiment & factual accuracy checks", "—", "—", "✓"],
              ["Prioritized fix list with estimated lift", "—", "—", "✓"],
            ].map(([label, a, b, c], i, arr) => (
              <div
                key={label}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1fr 1fr 1fr",
                  padding: "16px 24px",
                  borderBottom: i === arr.length - 1 ? "none" : "1px solid var(--aw-border)",
                  alignItems: "center",
                }}
              >
                <div style={{ fontSize: 14.5, color: "var(--aw-ink-2)" }}>{label}</div>
                {[a, b, c].map((cell, idx) => (
                  <div
                    key={idx}
                    className={cell === "✓" ? "check" : ""}
                    style={cell === "✓" ? {} : { color: "var(--aw-muted-2)" }}
                  >
                    {cell}
                  </div>
                ))}
              </div>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        style={{ padding: "96px 0", borderTop: "1px solid var(--aw-border)" }}
      >
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 items-start">
            <div className="aw-sticky">
              <div className="eyebrow mb-4">FAQ</div>
              <h2 className="aw-h1">
                Common
                <br />
                questions.
              </h2>
            </div>
            <div>
              {FAQ.map(([q, a], i) => (
                <details
                  key={q}
                  open={i === 0}
                  style={{ borderBottom: "1px solid var(--aw-border)", padding: "20px 0" }}
                >
                  <summary
                    data-testid={`faq-summary-${i}`}
                    style={{
                      cursor: "pointer",
                      listStyle: "none",
                      fontFamily: "var(--font-display)",
                      fontSize: 22,
                      lineHeight: 1.3,
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 24,
                      alignItems: "flex-start",
                      color: "var(--aw-ink)",
                    }}
                  >
                    {q}
                    <span
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: 22,
                        color: "var(--aw-muted)",
                        flexShrink: 0,
                      }}
                    >
                      +
                    </span>
                  </summary>
                  <div
                    style={{
                      paddingTop: 12,
                      color: "var(--aw-ink-2)",
                      fontSize: 15.5,
                      lineHeight: 1.6,
                      maxWidth: 740,
                    }}
                  >
                    {a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "96px 0" }}>
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="cta-strip">
            <div>
              <div
                className="eyebrow"
                style={{ color: "oklch(0.82 0.10 155)", marginBottom: 16 }}
              >
                Get started
              </div>
              <h2 className="aw-h1">
                Find out what AI is saying about you <em>this afternoon.</em>
              </h2>
            </div>
            <div className="flex flex-col gap-3 items-stretch">
              <Link
                to="/login"
                data-testid="cta-bottom-start"
                className="btn-aw"
                style={{
                  background: "white",
                  color: "var(--aw-ink)",
                  borderColor: "white",
                  height: 44,
                  justifyContent: "center",
                }}
              >
                Start free 14-day trial <span className="arrow">→</span>
              </Link>
              <Link
                to="/atlas"
                data-testid="cta-bottom-atlas"
                className="btn-aw btn-aw-ghost"
                style={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.2)",
                  height: 44,
                  justifyContent: "center",
                }}
              >
                Try the Free AI Atlas
              </Link>
              <div
                className="mono text-center"
                style={{ fontSize: 11.5, color: "#8A857A" }}
              >
                Up &amp; running in &lt; 10 minutes
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
